import type { FC } from 'react'
import { useState } from 'react'

import { InfoBox, Input, LoaderCircle } from '@skytech/ui'
import { countryOptions } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import AlertIcon from 'mdi-react/AlertIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import PolicyList from '@/components/policy/PolicyList'
import type { Policy } from '@/resources/ecom-policy'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { commonSubscriptionResources } from '@/resources/subscriptions'
import { getPolicyFilter } from '@/util/getPolicyFilter'
import handleMultipleChoiceItemSelect from '@/util/handleMultipleChoiceItemSelect'

import { useNewSubscriptionContext } from './NewSubscriptionForm.context'
import PolicyListTile from './PolicyListTile'

import './NewSubscriptionPolicies.styles.scss'

const NewSubscriptionPolicies: FC = () => {
  const [searchQuery, setSearchQuery] = useState('')

  const translate = useTranslation()

  const { data: ecomPolicies, isLoading: isEcomPoliciesLoading } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const { data: setup, isLoading: isSetupLoading } = useQuery(
    commonQueries.subscriptionSetup(),
    commonSubscriptionResources.getSetup
  )

  const newSub = useNewSubscriptionContext()
  const [devicePolicyId, setDevicePolicyId] = newSub.devicePolicyId
  const [telePolicyId, setTelePolicyId] = newSub.telePolicyId
  const [ecomPolicyIds, setEcomPolicyIds] = newSub.ecomPolicyIds
  const [departmentId] = newSub.departmentId
  const [allowSubscriptions] = newSub.allowSubscriptions
  const [subType] = newSub.subType

  const selectedDepartment = setup?.departments.find(({ id }) => id === departmentId)
  const selectedCustomerId = selectedDepartment?.customerId

  const filteredTelePolicyList =
    setup?.telePolicies.filter(
      ({ customerHeadId, customerId, departmentId: policyDepartmentId }) =>
        (customerHeadId || customerId === selectedCustomerId) && policyDepartmentId === departmentId
    ) || []
  const filteredDevicePolicyList =
    setup?.devicePolicies.filter(
      ({ customerHeadId, customerId, departmentId: policyDepartmentId }) =>
        (customerHeadId || customerId === selectedCustomerId) && policyDepartmentId === departmentId
    ) || []

  const extraPredicate = (policy: Policy) =>
    Boolean(policy.customerHeadName || policy.customerId === selectedCustomerId)

  const filteredEcomPolicyList = getPolicyFilter<Policy>({
    policyList: ecomPolicies,
    countryId: selectedDepartment?.countryId || 0,
    customerHasLeasing: selectedDepartment?.customerHasLeasing || false,
    extraPredicate,
  })

  return (
    <div className="new-subscription-policies">
      <div className="tiles">
        {isSetupLoading ? (
          <LoaderCircle />
        ) : (
          filteredTelePolicyList.length > 0 &&
          allowSubscriptions &&
          subType !== 'private' && (
            <div>
              <h3>
                <Translation id="Tele Policy (usage)" className="policy-tile-list-title" />
              </h3>

              <div className="new-subscription-policy-tile-list">
                {filteredTelePolicyList.map(policy => (
                  <PolicyListTile
                    key={policy.id}
                    onPolicyClick={selectedPolicy => setTelePolicyId(selectedPolicy || null)}
                    selectedPolicyId={telePolicyId || undefined}
                    {...policy}
                  />
                ))}
              </div>
            </div>
          )
        )}

        {isSetupLoading ? (
          <LoaderCircle />
        ) : (
          filteredDevicePolicyList.length > 0 && (
            <div>
              <h3>
                <Translation id="Device Policy (mobile phone)" className="policy-tile-list-title" />
              </h3>
              <div className="new-subscription-policy-tile-list">
                {filteredDevicePolicyList.map(policy => (
                  <PolicyListTile
                    key={policy.id}
                    onPolicyClick={selectedPolicy => setDevicePolicyId(selectedPolicy || null)}
                    selectedPolicyId={devicePolicyId || undefined}
                    {...policy}
                    coveredAmount={policy.coveredAmount}
                  />
                ))}
              </div>
            </div>
          )
        )}
      </div>

      {isEcomPoliciesLoading ? (
        <LoaderCircle />
      ) : filteredEcomPolicyList.length > 0 ? (
        <div className="new-subscription-ecom-policy-list">
          <div className="new-subscription-ecom-policy-list-title">
            <h3>
              <Translation id="Ecom Policy" />
            </h3>
          </div>

          <Input
            id="policy-search"
            marginBottom={16}
            type="text"
            onChange={e => setSearchQuery(e.target.value)}
            placeholder={translate('Search')}
          />

          <PolicyList
            isMultipleChoice
            selectedPolicyIds={ecomPolicyIds}
            searchQuery={searchQuery}
            policyList={filteredEcomPolicyList}
            onPolicySelect={id =>
              handleMultipleChoiceItemSelect({
                selectedId: id,
                currentIdList: ecomPolicyIds,
                setData: setEcomPolicyIds,
              })
            }
            onPoliciesSelect={setEcomPolicyIds}
          />
        </div>
      ) : (
        ecomPolicies?.length && (
          <div className="mx-auto w-full md:w-4/5 xl:w-3/5">
            <InfoBox emphasis="warning" marginTop={4} marginBottom={2}>
              <AlertIcon />
              <Translation
                id="No available policies for company. Make sure to have a policy available for {userCountry}"
                userCountry={
                  countryOptions.find(country => country.value === selectedDepartment?.countryId)
                    ?.label || ''
                }
              />
            </InfoBox>
          </div>
        )
      )}
    </div>
  )
}

export default NewSubscriptionPolicies
